// Epson Cloud Print Service
// Frontend API client for Epson printer management and cloud printing

import { authService } from './authService';
import { getServerURL } from './config';

const SERVER_URL = getServerURL();

export const epsonService = {
  // ===========================================
  // PRINTER CRUD
  // ===========================================

  /**
   * Get all registered printers
   */
  async getPrinters() {
    try {
      const response = await fetch(`${SERVER_URL}/epson/printers`, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch printers: ${response.status}`);
      }
      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching printers:', error);
      throw error;
    }
  },

  /**
   * Add a new printer
   * @param {Object} data - { name, model, epson_api_key, epson_client_id, epson_client_secret }
   */
  async addPrinter(data) {
    try {
      const response = await fetch(`${SERVER_URL}/epson/printers`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to add printer: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error adding printer:', error);
      throw error;
    }
  },

  /**
   * Update a printer
   * @param {string} printerId
   * @param {Object} data - Fields to update
   */
  async updatePrinter(printerId, data) {
    try {
      const response = await fetch(`${SERVER_URL}/epson/printers/${printerId}`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update printer: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating printer:', error);
      throw error;
    }
  },

  /**
   * Delete a printer
   * @param {string} printerId
   */
  async deletePrinter(printerId) {
    try {
      const response = await fetch(`${SERVER_URL}/epson/printers/${printerId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete printer: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting printer:', error);
      throw error;
    }
  },

  // ===========================================
  // AUTHORIZATION
  // ===========================================

  /**
   * Get the OAuth authorization URL for a printer
   * Frontend should open this URL in a new tab
   * @param {string} printerId
   * @returns {string} Authorization URL
   */
  async getAuthorizeUrl(printerId) {
    try {
      const response = await fetch(`${SERVER_URL}/epson/printers/${printerId}/authorize`, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to get authorize URL: ${response.status}`);
      }
      const result = await response.json();
      return result.data?.authorize_url;
    } catch (error) {
      console.error('Error getting authorize URL:', error);
      throw error;
    }
  },

  // ===========================================
  // PRINTER INFO & SETTINGS
  // ===========================================

  /**
   * Get live device info from Epson API
   * @param {string} printerId
   * @param {boolean} refresh - Force refresh from API
   */
  async getPrinterInfo(printerId, refresh = false) {
    try {
      const url = `${SERVER_URL}/epson/printers/${printerId}/info${refresh ? '?refresh=true' : ''}`;
      const response = await fetch(url, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to get printer info: ${response.status}`);
      }
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error getting printer info:', error);
      throw error;
    }
  },

  /**
   * Set a printer as default
   * @param {string} printerId
   */
  async setDefaultPrinter(printerId) {
    try {
      const response = await fetch(`${SERVER_URL}/epson/printers/${printerId}/default`, {
        method: 'PATCH',
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to set default printer: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error setting default printer:', error);
      throw error;
    }
  },

  /**
   * Manually refresh a printer's token
   * @param {string} printerId
   */
  async refreshToken(printerId) {
    try {
      const response = await fetch(`${SERVER_URL}/epson/printers/${printerId}/refresh-token`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to refresh token: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  },

  // ===========================================
  // PRINTING
  // ===========================================

  /**
   * Print a generation on a specific printer
   * @param {string} generationId
   * @param {string} printerId
   */
  async printGeneration(generationId, printerId) {
    try {
      const response = await fetch(`${SERVER_URL}/epson/print/${generationId}`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ printerId }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to print: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error printing generation:', error);
      throw error;
    }
  },
};
